if (!window.Eurus) window.Eurus = {};
if (!window.Eurus.loadedScript) window.Eurus.loadedScript = new Set();
if (!window.Eurus.loadedScript.has('okendo-reviews-theme.v7.js')) {
  window.Eurus.loadedScript.add('okendo-reviews-theme.v7.js');

  const API_ORIGIN = 'https://api.okendo.io/v1';
  const DEFAULT_PAGE_SIZE = 5;
  const VALID_ORDER_BY = new Set([
    'date desc',
    'date asc',
    'rating desc',
    'rating asc',
    'helpful desc',
    'helpful asc',
    'has_media desc',
    'unhelpful desc',
  ]);

  const dismissLoadingAlerts = (section) => {
    section.querySelectorAll('.oke-alert').forEach((alert) => {
      alert.setAttribute('aria-hidden', 'true');
      alert.hidden = true;
    });
  };

  const getWidget = (section) => section.querySelector('.okendo-reviews-widget');

  const getModal = (section) => section.querySelector('.okendo-reviews-modal');

  const normalizeOrderBy = (value) => {
    if (!value) return 'date desc';

    let normalized = String(value);
    try {
      let guard = 0;
      while (normalized.includes('%') && guard < 5) {
        const decoded = decodeURIComponent(normalized);
        if (decoded === normalized) break;
        normalized = decoded;
        guard += 1;
      }
    } catch {
      /* keep value */
    }

    normalized = normalized.replace(/\+/g, ' ').trim().toLowerCase();
    return VALID_ORDER_BY.has(normalized) ? normalized : 'date desc';
  };

  const getMetafieldData = (widget) => {
    const script = widget?.querySelector('script[data-oke-metafield-data]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  };

  const resolveReviewsApiBase = (section, widget) => {
    let apiBase = getReviewsApiBase(widget);
    if (apiBase) {
      apiBase = apiBase.replace(/\/v1\/v1\//, '/v1/');
      section.dataset.okendoReviewsApiBase = apiBase;
    }
    return apiBase;
  };

  const extractReviewsApiBase = (input) => {
    if (!input) return null;

    const absolute = String(input).match(
      /https:\/\/api\.okendo\.io\/v1\/stores\/[^/?]+\/products\/[^/?]+\/reviews/
    );
    if (absolute) return absolute[0];

    try {
      const url = new URL(input, `${API_ORIGIN}/`);
      const pathMatch = url.pathname.match(/\/stores\/[^/]+\/products\/[^/]+\/reviews/);
      if (pathMatch) return `${API_ORIGIN}${pathMatch[0]}`;
    } catch {
      /* continue */
    }

    return null;
  };

  const getReviewsApiBase = (widget) => {
    const data = getMetafieldData(widget);

    const fromNextUrl = extractReviewsApiBase(data?.reviewsNextUrl);
    if (fromNextUrl) return fromNextUrl;

    const showMoreHref = widget?.querySelector('.oke-showMore a')?.getAttribute('href');
    const fromShowMore = extractReviewsApiBase(parseNextUrl(showMoreHref));
    if (fromShowMore) return fromShowMore;

    const review = data?.reviews?.[0];
    if (review?.subscriberId && review?.productId) {
      return `${API_ORIGIN}/stores/${review.subscriberId}/products/${review.productId}/reviews`;
    }

    console.warn('[okendo-reviews-theme] Could not resolve reviews API base URL');
    return null;
  };

  const parseNextUrl = (href) => {
    if (!href) return null;
    try {
      const url = new URL(href, window.location.origin);
      let next = url.searchParams.get('okeReviewsNextUrl');
      if (!next) return null;
      let guard = 0;
      while (next && !next.startsWith('https://') && guard < 5) {
        next = decodeURIComponent(next);
        guard += 1;
      }
      return next.startsWith('https://') ? next : null;
    } catch {
      return null;
    }
  };

  const ensureOrderByInUrl = (nextUrl, orderBy) => {
    if (!nextUrl) return nextUrl;
    try {
      const url = new URL(nextUrl);
      if (!url.searchParams.has('orderBy')) {
        url.searchParams.set('orderBy', normalizeOrderBy(orderBy));
      }
      return url.toString();
    } catch {
      return nextUrl;
    }
  };

  const buildShowMoreHref = (nextUrl, pageNum) => {
    const encoded = encodeURIComponent(encodeURIComponent(nextUrl));
    return `?okeReviewsNextUrl=${encoded}&okeReviewsPageNum=${pageNum}`;
  };

  const formatRelativeDate = (isoDate) => {
    const diffMs = Date.now() - new Date(isoDate).getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days < 1) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) { const w = Math.floor(days / 7); return `${w} ${w === 1 ? 'week' : 'weeks'} ago`; }
    if (days < 365) { const m = Math.floor(days / 30); return `${m} ${m === 1 ? 'month' : 'months'} ago`; }
    { const y = Math.floor(days / 365); return `${y} ${y === 1 ? 'year' : 'years'} ago`; }
  };

  const stripIds = (root) => {
    root.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  };

  const setText = (root, selector, text) => {
    const el = root.querySelector(selector);
    if (el) el.textContent = text;
  };

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const renderAttributes = (root, attributes) => {
    const container = root.querySelector('.oke-w-selectAttr');
    const wrap = root.querySelector('.oke-w-review-selectAttributes');
    if (!container || !wrap) return;

    if (!attributes || attributes.length === 0) {
      wrap.hidden = true;
      return;
    }

    wrap.hidden = false;
    container.innerHTML = attributes
      .map(
        (attr) => `<div class="oke-w-selectAttr-item">
          <strong class="oke-w-selectAttr-item-title">${escapeHtml(attr.title)}</strong>
          <span class="oke-w-selectAttr-item-value">${escapeHtml(attr.value)}</span>
        </div>`
      )
      .join('');
  };

  const renderRecommendation = (root, isRecommended) => {
    const block = root.querySelector('.oke-w-review-recommendation');
    const el = root.querySelector('.oke-w-reviewerRecommendation');
    const text = root.querySelector('.oke-w-reviewerRecommendation-text');
    if (!block || !el || !text) return;

    block.hidden = false;
    el.classList.toggle('oke-w-reviewerRecommendation--yes', Boolean(isRecommended));
    text.textContent = isRecommended
      ? 'I recommend this product'
      : 'I do not recommend this product';
  };

  const renderStars = (root, rating) => {
    const foreground = root.querySelector('.oke-stars-foreground');
    const a11y = root.querySelector('.oke-reviewContent-stars .oke-a11yText');
    const width = `${Math.max(0, Math.min(100, (rating / 5) * 100))}%`;
    if (foreground) foreground.style.width = width;
    if (a11y) a11y.textContent = `Rated ${rating} out of 5 stars`;
  };

  const renderHelpful = (root, review) => {
    const counters = root.querySelectorAll('.oke-helpful-vote-counter');
    const counts = [review.helpfulCount || 0, review.unhelpfulCount || 0];
    counters.forEach((counter, index) => {
      const a11y = [...counter.querySelectorAll('.oke-a11yText')];
      counter.textContent = '';
      a11y.forEach((node, i) => {
        counter.appendChild(node);
        if (i === 0) counter.appendChild(document.createTextNode(` ${counts[index] || 0} `));
      });
    });
  };

  const cloneReviewItem = (templateItem, review) => {
    const item = templateItem.cloneNode(true);
    stripIds(item);

    setText(item, '.oke-w-reviewer-name', review.reviewer?.displayName?.trim() || '');

    const verified = item.querySelector('.oke-w-reviewer-verified');
    if (verified) verified.hidden = !review.reviewer?.isVerified;

    renderRecommendation(item, review.isRecommended);
    renderAttributes(item, review.reviewer?.attributes);
    renderStars(item, review.rating || 0);
    setText(item, '.oke-reviewContent-date span', formatRelativeDate(review.dateCreated));

    const title = item.querySelector('.oke-reviewContent-title');
    if (title) {
      title.textContent = review.title || '';
      title.hidden = !review.title;
    }

    const body = item.querySelector('.oke-reviewContent-body p');
    if (body) body.textContent = review.body || '';

    renderHelpful(item, review);

    return item;
  };

  const renderReviewsToList = (list, reviews, templateItem) => {
    if (!list || !templateItem) return;
    const template = templateItem.cloneNode(true);
    list.innerHTML = '';
    reviews.forEach((review) => {
      list.appendChild(cloneReviewItem(template, review));
    });
    setupReviewsSwiper(list);
  };

  const updateShowMoreButton = (footer, nextUrl, pageNum = 2) => {
    const showMore = footer?.querySelector('.oke-showMore a, .oke-showMore-button');
    if (!showMore) return;

    if (nextUrl) {
      const absolute = nextUrl.startsWith('http') ? nextUrl : `${API_ORIGIN}${nextUrl}`;
      showMore.href = buildShowMoreHref(absolute, pageNum);
      showMore.closest('.oke-showMore')?.removeAttribute('hidden');
    } else {
      const wrap = showMore.closest('.oke-showMore');
      if (wrap) wrap.hidden = true;
    }
  };

  const updateModalShowMore = (section, nextUrl, pageNum = 2) => {
    const modal = getModal(section);
    const trigger = modal?.querySelector('.okendo-reviews-modal__load-more');
    const wrap = trigger?.closest('.oke-showMore');
    if (!trigger) return;

    section.dataset.okendoModalNextUrl = nextUrl || '';

    if (nextUrl) {
      const absolute = nextUrl.startsWith('http') ? nextUrl : `${API_ORIGIN}${nextUrl}`;
      trigger.href = buildShowMoreHref(absolute, pageNum);
      if (wrap) wrap.hidden = false;
    } else if (wrap) {
      wrap.hidden = true;
    }
  };

  const setListLoading = (list, isLoading) => {
    if (!list) return;
    list.classList.toggle('okendo-reviews-list--loading', isLoading);
    list.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  };

  const setShowMoreLoading = (trigger, isLoading) => {
    if (!trigger) return;
    trigger.classList.toggle('oke-is-loading', isLoading);
    trigger.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    const label = trigger.querySelector('.oke-showMore-button-text, .oke-button-text');
    if (!label) return;
    if (isLoading) {
      if (!trigger.dataset.okendoOriginalLabel) {
        trigger.dataset.okendoOriginalLabel = label.textContent.trim();
      }
      label.textContent = 'Loading...';
    } else if (trigger.dataset.okendoOriginalLabel) {
      label.textContent = trigger.dataset.okendoOriginalLabel;
      delete trigger.dataset.okendoOriginalLabel;
    }
  };

  const renderReviews = (widget, reviews, nextUrl) => {
    const list = widget.querySelector('.oke-w-reviews-list');
    const footer = widget.querySelector('.oke-w-reviews-footer');
    const existingItem = list?.querySelector('.oke-w-reviews-list-item');
    if (!list || !existingItem) return;

    renderReviewsToList(list, reviews, existingItem);
    updateShowMoreButton(footer, nextUrl, 2);
  };

  const MOBILE_SWIPER_MQ = window.matchMedia('(max-width: 767px)');
  const DESKTOP_SLIDES_PER_VIEW = 3;
  const SWIPER_GAP = { mobile: 12, desktop: 20 };
  const SWIPER_ARROW_PREV =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 6L9 12L15 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const SWIPER_ARROW_NEXT =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const swiperObservers = new WeakMap();
  const swiperResizeObservers = new WeakMap();

  const getSwiperConfig = () => {
    const isMobile = MOBILE_SWIPER_MQ.matches;
    return {
      isMobile,
      slidesPerView: isMobile ? 1 : DESKTOP_SLIDES_PER_VIEW,
      gap: isMobile ? SWIPER_GAP.mobile : SWIPER_GAP.desktop,
    };
  };

  const getSwiperPageCount = (itemCount, config) =>
    config.isMobile ? itemCount : Math.max(1, Math.ceil(itemCount / config.slidesPerView));

  const teardownSwiperObserver = (list) => {
    const observer = swiperObservers.get(list);
    if (!observer) return;
    observer.disconnect();
    swiperObservers.delete(list);
  };

  const getSwiperViewport = (swiper) =>
    swiper?.querySelector('.okendo-reviews-swiper__viewport');

  const getSwiperItems = (swiper) => {
    const list = getSwiperViewport(swiper)?.querySelector('.oke-w-reviews-list');
    return list ? [...list.querySelectorAll('.oke-w-reviews-list-item')] : [];
  };

  const syncSwiperSlideWidth = (swiper) => {
    const viewport = getSwiperViewport(swiper);
    if (!viewport || viewport.clientWidth < 1) return;

    const config = getSwiperConfig();
    const slideWidth =
      (viewport.clientWidth - config.gap * (config.slidesPerView - 1)) / config.slidesPerView;

    swiper.style.setProperty('--okendo-slide-width', `${slideWidth}px`);
    swiper.style.setProperty('--okendo-swiper-gap', `${config.gap}px`);
    swiper.classList.toggle('okendo-reviews-swiper--desktop', !config.isMobile);
    swiper.classList.toggle('okendo-reviews-swiper--mobile', config.isMobile);
    swiper.dataset.slidesPerView = String(config.slidesPerView);
    swiper.dataset.swiperMode = config.isMobile ? 'mobile' : 'desktop';
  };

  const setActiveSwiperDot = (pagination, index) => {
    pagination.querySelectorAll('.okendo-reviews-swiper__dot').forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  };

  const rebuildSwiperPagination = (swiper, items) => {
    const pagination = swiper.querySelector('.okendo-reviews-swiper__pagination');
    if (!pagination) return;

    const config = getSwiperConfig();
    const dotCount = getSwiperPageCount(items.length, config);

    if (config.isMobile) {
      pagination.innerHTML = [...items]
        .map(
          (_, index) =>
            `<button type="button" class="okendo-reviews-swiper__dot${index === 0 ? ' is-active' : ''}" data-index="${index}" role="tab" aria-selected="${index === 0 ? 'true' : 'false'}" aria-label="Review ${index + 1}"></button>`
        )
        .join('');
    } else {
      pagination.innerHTML = Array.from({ length: dotCount }, (_, page) => {
        const start = page * config.slidesPerView + 1;
        const end = Math.min((page + 1) * config.slidesPerView, items.length);
        const label = start === end ? `Review ${start}` : `Reviews ${start}-${end}`;
        return `<button type="button" class="okendo-reviews-swiper__dot${page === 0 ? ' is-active' : ''}" data-page="${page}" role="tab" aria-selected="${page === 0 ? 'true' : 'false'}" aria-label="${label}"></button>`;
      }).join('');
    }
  };

  const getSwiperActiveDotIndex = (viewport, items, config) => {
    if (!items.length) return 0;

    if (config.isMobile) {
      const center = viewport.scrollLeft + viewport.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      items.forEach((item, index) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const distance = Math.abs(center - itemCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      return closestIndex;
    }

    let firstVisibleIndex = 0;
    for (let index = 0; index < items.length; index += 1) {
      if (items[index].offsetLeft + items[index].offsetWidth > viewport.scrollLeft + 8) {
        firstVisibleIndex = index;
        break;
      }
    }

    return Math.min(
      Math.floor(firstVisibleIndex / config.slidesPerView),
      getSwiperPageCount(items.length, config) - 1
    );
  };

  const scrollSwiperToDot = (swiper, dotIndex) => {
    const viewport = getSwiperViewport(swiper);
    const items = getSwiperItems(swiper);
    const config = getSwiperConfig();
    if (!viewport || !items.length) return;

    if (config.isMobile) {
      const targetItem = items[dotIndex];
      if (!targetItem) return;
      viewport.scrollTo({
        left: targetItem.offsetLeft - (viewport.clientWidth - targetItem.offsetWidth) / 2,
        behavior: 'smooth',
      });
      return;
    }

    const targetIndex = Math.min(dotIndex * config.slidesPerView, items.length - 1);
    const targetItem = items[targetIndex];
    if (!targetItem) return;
    viewport.scrollTo({ left: targetItem.offsetLeft, behavior: 'smooth' });
  };

  const scrollReviewsSwiper = (swiper, direction) => {
    const viewport = getSwiperViewport(swiper);
    const items = getSwiperItems(swiper);
    const config = getSwiperConfig();
    if (!viewport || !items.length) return;

    const slideWidth = items[0].offsetWidth;
    const step = config.isMobile
      ? slideWidth + config.gap
      : (slideWidth + config.gap) * config.slidesPerView;
    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const nextLeft = Math.max(0, Math.min(maxScroll, viewport.scrollLeft + direction * step));

    viewport.scrollTo({ left: nextLeft, behavior: 'smooth' });
    window.setTimeout(() => updateSwiperControls(swiper), 200);
  };

  const updateSwiperControls = (swiper) => {
    const viewport = getSwiperViewport(swiper);
    const pagination = swiper.querySelector('.okendo-reviews-swiper__pagination');
    const items = getSwiperItems(swiper);
    const config = getSwiperConfig();
    if (!viewport) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const canScroll = maxScroll > 4;

    swiper.querySelectorAll('.okendo-reviews-swiper__arrow').forEach((arrow) => {
      arrow.hidden = !canScroll;
    });

    const prev = swiper.querySelector('.okendo-reviews-swiper__arrow--prev');
    const next = swiper.querySelector('.okendo-reviews-swiper__arrow--next');
    if (prev) prev.disabled = !canScroll || viewport.scrollLeft <= 4;
    if (next) next.disabled = !canScroll || viewport.scrollLeft >= maxScroll - 4;

    if (pagination && items.length && !pagination.hidden) {
      setActiveSwiperDot(pagination, getSwiperActiveDotIndex(viewport, items, config));
    }
  };

  const bindSwiperViewportResize = (swiper) => {
    if (swiperResizeObservers.has(swiper)) return;

    const viewport = getSwiperViewport(swiper);
    if (!viewport) return;

    const observer = new ResizeObserver(() => {
      syncSwiperSlideWidth(swiper);
      updateSwiperControls(swiper);
    });
    observer.observe(viewport);
    swiperResizeObservers.set(swiper, observer);
  };

  const bindSwiperScrollSync = (swiper, list) => {
    teardownSwiperObserver(list);

    const viewport = getSwiperViewport(swiper) || list;
    const pagination = swiper.querySelector('.okendo-reviews-swiper__pagination');
    const items = getSwiperItems(swiper);
    if (!pagination || !items.length) return;

    syncSwiperSlideWidth(swiper);
    const config = getSwiperConfig();

    const observer = new IntersectionObserver(
      (entries) => {
        if (!getSwiperConfig().isMobile) return;
        let best = null;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const currentItems = getSwiperItems(swiper);
          const index = currentItems.indexOf(entry.target);
          if (index < 0) return;
          if (!best || entry.intersectionRatio > best.ratio) {
            best = { index, ratio: entry.intersectionRatio };
          }
        });
        if (best && best.index >= 0) setActiveSwiperDot(pagination, best.index);
      },
      { root: viewport, threshold: [0.45, 0.6, 0.75] }
    );

    if (config.isMobile) {
      items.forEach((item) => observer.observe(item));
    }
    swiperObservers.set(list, observer);

    if (viewport.dataset.okendoSwiperScrollBound !== 'true') {
      viewport.dataset.okendoSwiperScrollBound = 'true';
      let scrollTimer;
      viewport.addEventListener(
        'scroll',
        () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => updateSwiperControls(swiper), 80);
        },
        { passive: true }
      );
    }

    if (pagination.dataset.okendoSwiperBound !== 'true') {
      pagination.dataset.okendoSwiperBound = 'true';
      pagination.addEventListener('click', (event) => {
        const dot = event.target.closest('.okendo-reviews-swiper__dot');
        if (!dot) return;

        const activeConfig = getSwiperConfig();
        const dotIndex = activeConfig.isMobile
          ? Number.parseInt(dot.dataset.index, 10)
          : Number.parseInt(dot.dataset.page, 10);

        scrollSwiperToDot(swiper, dotIndex);
        setActiveSwiperDot(pagination, dotIndex);
      });
    }

    updateSwiperControls(swiper);
  };

  const createSwiperArrow = (direction) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `okendo-reviews-swiper__arrow okendo-reviews-swiper__arrow--${direction}`;
    button.setAttribute(
      'aria-label',
      direction === 'prev' ? 'Previous reviews' : 'Next reviews'
    );
    button.innerHTML = direction === 'prev' ? SWIPER_ARROW_PREV : SWIPER_ARROW_NEXT;
    return button;
  };

  const ensureSwiperNav = (swiper) => {
    const viewport = getSwiperViewport(swiper);
    if (!viewport) return;

    let nav = swiper.querySelector('.okendo-reviews-swiper__nav');
    if (!nav) {
      nav = document.createElement('div');
      nav.className = 'okendo-reviews-swiper__nav';
      const prev = createSwiperArrow('prev');
      const next = createSwiperArrow('next');

      swiper.insertBefore(nav, viewport);
      nav.appendChild(prev);
      nav.appendChild(viewport);
      nav.appendChild(next);
    }

    if (swiper.dataset.okendoSwiperArrowsBound !== 'true') {
      swiper.dataset.okendoSwiperArrowsBound = 'true';
      swiper.addEventListener('click', (event) => {
        if (event.target.closest('.okendo-reviews-swiper__arrow--prev')) {
          event.preventDefault();
          scrollReviewsSwiper(swiper, -1);
        } else if (event.target.closest('.okendo-reviews-swiper__arrow--next')) {
          event.preventDefault();
          scrollReviewsSwiper(swiper, 1);
        }
      });
    }
  };

  const setupReviewsSwiper = (list) => {
    if (!list || list.closest('.okendo-reviews-modal')) return;

    let swiper = list.closest('.okendo-reviews-swiper');
    if (!swiper) {
      swiper = document.createElement('div');
      swiper.className = 'okendo-reviews-swiper';
      list.parentNode.insertBefore(swiper, list);

      const nav = document.createElement('div');
      nav.className = 'okendo-reviews-swiper__nav';

      const viewport = document.createElement('div');
      viewport.className = 'okendo-reviews-swiper__viewport';

      nav.appendChild(createSwiperArrow('prev'));
      nav.appendChild(viewport);
      nav.appendChild(createSwiperArrow('next'));
      viewport.appendChild(list);

      const pagination = document.createElement('div');
      pagination.className = 'okendo-reviews-swiper__pagination';
      pagination.setAttribute('role', 'tablist');
      pagination.setAttribute('aria-label', 'Yorumlar');

      swiper.appendChild(nav);
      swiper.appendChild(pagination);
      swiper.dataset.okendoSwiperArrowsBound = 'true';
      swiper.addEventListener('click', (event) => {
        if (event.target.closest('.okendo-reviews-swiper__arrow--prev')) {
          event.preventDefault();
          scrollReviewsSwiper(swiper, -1);
        } else if (event.target.closest('.okendo-reviews-swiper__arrow--next')) {
          event.preventDefault();
          scrollReviewsSwiper(swiper, 1);
        }
      });
    } else {
      if (!getSwiperViewport(swiper)) {
        const viewport = document.createElement('div');
        viewport.className = 'okendo-reviews-swiper__viewport';
        swiper.insertBefore(viewport, list);
        viewport.appendChild(list);
      }
      ensureSwiperNav(swiper);
    }

    const pagination = swiper.querySelector('.okendo-reviews-swiper__pagination');
    const items = getSwiperItems(swiper);
    const config = getSwiperConfig();

    if (!items.length) {
      swiper.classList.remove('okendo-reviews-swiper--active');
      if (pagination) {
        pagination.innerHTML = '';
        pagination.hidden = true;
      }
      teardownSwiperObserver(list);
      getSwiperViewport(swiper)?.scrollTo?.({ left: 0 });
      return;
    }

    swiper.classList.add('okendo-reviews-swiper--active');
    syncSwiperSlideWidth(swiper);

    const expectedDots = getSwiperPageCount(items.length, config);
    const currentCount = pagination?.querySelectorAll('.okendo-reviews-swiper__dot').length || 0;
    if (
      pagination &&
      (currentCount !== expectedDots ||
        swiper.dataset.swiperMode !== (config.isMobile ? 'mobile' : 'desktop'))
    ) {
      rebuildSwiperPagination(swiper, items);
    }

    if (pagination) pagination.hidden = items.length <= config.slidesPerView;
    getSwiperViewport(swiper)?.scrollTo?.({ left: 0 });
    bindSwiperViewportResize(swiper);
    bindSwiperScrollSync(swiper, list);
    window.requestAnimationFrame(() => {
      syncSwiperSlideWidth(swiper);
      updateSwiperControls(swiper);
    });
  };

  const watchReviewsList = (section) => {
    const widget = getWidget(section);
    const list = widget?.querySelector('.oke-w-reviews-list');
    if (!list || list.dataset.okendoSwiperWatch === 'true') return;
    list.dataset.okendoSwiperWatch = 'true';

    if (list.querySelector('.oke-w-reviews-list-item')) {
      setupReviewsSwiper(list);
    }

    const observer = new MutationObserver(() => {
      if (list.querySelector('.oke-w-reviews-list-item')) {
        setupReviewsSwiper(list);
      }
    });
    observer.observe(list, { childList: true });
  };

  const refreshOkendoReviewsSwipers = (root = document) => {
    root.querySelectorAll('.okendo-reviews .oke-w-reviews-list').forEach((list) => {
      if (!list.closest('.okendo-reviews-modal')) {
        setupReviewsSwiper(list);
      }
    });
  };

  if (!window.Eurus.okendoReviewsSwiperResize) {
    window.Eurus.okendoReviewsSwiperResize = true;
    MOBILE_SWIPER_MQ.addEventListener('change', () => refreshOkendoReviewsSwipers());
    window.addEventListener('resize', () => refreshOkendoReviewsSwipers(), { passive: true });
    document.addEventListener('okendo-reviews:refresh', (event) => {
      refreshOkendoReviewsSwipers(event.detail?.root || document);
    });
  }

  const fetchReviews = async (apiBase, orderBy, limit = DEFAULT_PAGE_SIZE) => {
    const normalized = normalizeOrderBy(orderBy);
    const url = `${apiBase}?limit=${limit}&orderBy=${encodeURIComponent(normalized)}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Okendo API ${response.status}`);
    return response.json();
  };

  const setSelectOrderBy = (select, orderBy) => {
    if (!select) return;
    const normalized = normalizeOrderBy(orderBy);
    const option = [...select.options].find(
      (opt) => normalizeOrderBy(opt.value) === normalized
    );
    if (option) select.value = option.value;
  };

  const syncSortSelects = (section, orderBy) => {
    const widget = getWidget(section);
    const modal = getModal(section);

    widget
      ?.querySelectorAll('.oke-sortSelect-sort, #oke-sortSelect--reviews')
      .forEach((select) => setSelectOrderBy(select, orderBy));

    modal
      ?.querySelectorAll('.oke-sortSelect-sort, #oke-sortSelect--reviews')
      .forEach((select) => setSelectOrderBy(select, orderBy));

    section.querySelectorAll('[data-okendo-sort-picker]').forEach(updateMobileSortPickerUI);
  };

  const closeMobileSortPicker = (picker) => {
    if (!picker) return;
    picker.classList.remove('is-open');
    picker.querySelector('.okendo-sort-picker__sheet')?.setAttribute('hidden', '');
    picker.querySelector('.okendo-sort-picker__trigger')?.setAttribute('aria-expanded', 'false');
  };

  const openMobileSortPicker = (picker) => {
    if (!picker) return;
    document.querySelectorAll('.okendo-sort-picker.is-open').forEach(closeMobileSortPicker);
    picker.querySelector('.okendo-sort-picker__sheet')?.removeAttribute('hidden');
    picker.classList.add('is-open');
    picker.querySelector('.okendo-sort-picker__trigger')?.setAttribute('aria-expanded', 'true');
  };

  const updateMobileSortPickerUI = (picker) => {
    if (!picker) return;
    const sortWrap = picker.closest('.oke-sortSelect, .oke-w-reviews-sort');
    const select = sortWrap?.querySelector('.oke-sortSelect-sort, #oke-sortSelect--reviews');
    if (!select) return;

    const valueEl = picker.querySelector('.okendo-sort-picker__value');
    const trigger = picker.querySelector('.okendo-sort-picker__trigger');
    const selected = select.options[select.selectedIndex];

    if (valueEl && selected) {
      valueEl.textContent = selected.textContent.trim();
    }

    picker.querySelectorAll('.okendo-sort-picker__option').forEach((btn) => {
      const isSelected =
        normalizeOrderBy(btn.dataset.value) === normalizeOrderBy(select.value);
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    if (trigger) trigger.disabled = select.disabled;
  };

  const setupMobileSortPicker = (section, select, onChange) => {
    if (!select || select.dataset.okendoMobileSortInit === 'true') return;

    const sortWrap = select.closest('.oke-sortSelect') || select.closest('.oke-w-reviews-sort');
    if (!sortWrap || sortWrap.querySelector('[data-okendo-sort-picker]')) return;

    select.dataset.okendoMobileSortInit = 'true';

    const label =
      sortWrap.querySelector('.oke-sortSelect-label')?.textContent?.trim() || 'Sort';
    const selectedOption = select.options[select.selectedIndex];

    const picker = document.createElement('div');
    picker.className = 'okendo-sort-picker';
    picker.dataset.okendoSortPicker = 'true';

    const optionsHtml = [...select.options]
      .map(
        (opt) =>
          `<button type="button" class="okendo-sort-picker__option" data-value="${escapeHtml(opt.value)}" role="option">${escapeHtml(opt.textContent.trim())}</button>`
      )
      .join('');

    picker.innerHTML = `<button type="button" class="okendo-sort-picker__trigger" aria-haspopup="listbox" aria-expanded="false">
        <span class="okendo-sort-picker__trigger-main">
          <span class="okendo-sort-picker__value">${escapeHtml(selectedOption?.textContent?.trim() || '')}</span>
          <span class="okendo-sort-picker__label">${escapeHtml(label)}</span>
        </span>
        <svg class="okendo-sort-picker__chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <div class="okendo-sort-picker__sheet" hidden>
        <div class="okendo-sort-picker__overlay" data-okendo-sort-close></div>
        <div class="okendo-sort-picker__panel" role="listbox" aria-label="${escapeHtml(label)}">
          <div class="okendo-sort-picker__header">${escapeHtml(label)}</div>
          <div class="okendo-sort-picker__options">${optionsHtml}</div>
        </div>
      </div>`;

    sortWrap.appendChild(picker);
    sortWrap.classList.add('okendo-sort--has-picker');

    const trigger = picker.querySelector('.okendo-sort-picker__trigger');

    trigger.addEventListener('click', () => {
      if (select.disabled) return;
      if (picker.classList.contains('is-open')) closeMobileSortPicker(picker);
      else openMobileSortPicker(picker);
    });

    picker.querySelector('[data-okendo-sort-close]')?.addEventListener('click', () => {
      closeMobileSortPicker(picker);
    });

    picker.querySelectorAll('.okendo-sort-picker__option').forEach((btn) => {
      btn.addEventListener('click', () => {
        setSelectOrderBy(select, btn.dataset.value);
        updateMobileSortPickerUI(picker);
        closeMobileSortPicker(picker);
        onChange(select);
      });
    });

    updateMobileSortPickerUI(picker);
  };

  const setSortControlsLoading = (section, select, isLoading) => {
    if (select) select.disabled = isLoading;
    const sortWrap = select?.closest('.oke-sortSelect, .oke-w-reviews-sort');
    sortWrap?.querySelector('[data-okendo-sort-picker]')?.classList.toggle('is-loading', isLoading);
    sortWrap?.querySelector('.okendo-sort-picker__trigger')?.toggleAttribute('disabled', isLoading);
  };

  const applyReviewsResponse = (section, widget, data, target = 'inline') => {
    const reviews = data.reviews || [];
    const isModal = target === 'modal';
    const modal = getModal(section);

    if (isModal) {
      const list = modal?.querySelector('.okendo-reviews-modal__list');
      const templateItem = list?.querySelector('.oke-w-reviews-list-item') ||
        widget?.querySelector('.oke-w-reviews-list .oke-w-reviews-list-item');
      if (list && templateItem) {
        renderReviewsToList(list, reviews, templateItem);
      }
      updateModalShowMore(section, data.nextUrl, 2);
    } else {
      renderReviews(widget, reviews, data.nextUrl);
    }

    if (!isModal) {
      const countEl = widget.querySelector('.oke-w-reviews-count');
      const totalCount = getMetafieldData(widget)?.reviewAggregate?.reviewCount;
      if (countEl) {
        countEl.textContent = (function(n){ return `${n} ${n === 1 ? 'review' : 'reviews'}`; })(totalCount || reviews.length);
      }
    }

    section.dataset.okendoCurrentSort = normalizeOrderBy(
      widget?.querySelector('.oke-sortSelect-sort, #oke-sortSelect--reviews')?.value ||
        section.dataset.okendoCurrentSort
    );
  };

  const sortReviews = async (section, orderBy, target = 'inline') => {
    const widget = getWidget(section);
    const modal = getModal(section);
    const normalizedOrder = normalizeOrderBy(orderBy);
    const isModal = target === 'modal';
    const select = isModal
      ? modal?.querySelector('.oke-sortSelect-sort, #oke-sortSelect--reviews')
      : widget?.querySelector('.oke-sortSelect-sort, #oke-sortSelect--reviews');
    const apiBase = resolveReviewsApiBase(section, widget);

    if (!widget || !apiBase) {
      console.warn('[okendo-reviews-theme] Could not resolve reviews API base URL');
      return;
    }

    section.dataset.okendoReviewsApiBase = apiBase;

    const list = isModal
      ? modal?.querySelector('.okendo-reviews-modal__list')
      : widget?.querySelector('.oke-w-reviews-list');
    setListLoading(list, true);
    setSortControlsLoading(section, select, true);

    try {
      const data = await fetchReviews(apiBase, normalizedOrder);
      applyReviewsResponse(section, widget, data, target);
      section.dataset.okendoCurrentSort = normalizedOrder;
      syncSortSelects(section, normalizedOrder);
    } catch (error) {
      console.error('[okendo-reviews-theme] Sort failed:', error);
      if (select && section.dataset.okendoCurrentSort) {
        setSelectOrderBy(select, section.dataset.okendoCurrentSort);
        section.querySelectorAll('[data-okendo-sort-picker]').forEach(updateMobileSortPickerUI);
      }
    } finally {
      setListLoading(list, false);
      setSortControlsLoading(section, select, false);
    }
  };

  const loadMoreReviews = async (section, trigger, target = 'modal') => {
    const href = trigger.getAttribute('href');
    let nextUrl = parseNextUrl(href);
    if (!nextUrl) return;

    nextUrl = ensureOrderByInUrl(nextUrl, section.dataset.okendoCurrentSort);

    const widget = getWidget(section);
    const modal = getModal(section);
    const isModal = target === 'modal';
    const list = isModal
      ? modal?.querySelector('.okendo-reviews-modal__list')
      : widget?.querySelector('.oke-w-reviews-list');
    const templateItem =
      list?.querySelector('.oke-w-reviews-list-item') ||
      widget?.querySelector('.oke-w-reviews-list .oke-w-reviews-list-item');
    const footer = isModal ? null : widget?.querySelector('.oke-w-reviews-footer');

    if (!widget || !list || !templateItem) return;

    if (trigger.dataset.okendoLoading === 'true') return;
    trigger.dataset.okendoLoading = 'true';
    setShowMoreLoading(trigger, true);

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error(`Okendo API ${response.status}`);
      const data = await response.json();
      const reviews = data.reviews || [];
      const template = templateItem.cloneNode(true);

      reviews.forEach((review) => {
        list.appendChild(cloneReviewItem(template, review));
      });

      const pageNum =
        parseInt(new URL(href, window.location.origin).searchParams.get('okeReviewsPageNum') || '1', 10) + 1;

      if (isModal) {
        updateModalShowMore(section, data.nextUrl, pageNum);
      } else {
        updateShowMoreButton(footer, data.nextUrl, pageNum);
      }
    } catch (error) {
      console.error('[okendo-reviews-theme] Load more failed:', error);
    } finally {
      setShowMoreLoading(trigger, false);
      delete trigger.dataset.okendoLoading;
    }
  };

  const setupModalSort = (section, widget, modal) => {
    const controls = modal.querySelector('.okendo-reviews-modal__controls');
    const widgetSortWrap = widget?.querySelector('.oke-sortSelect, .oke-w-reviews-sort');
    const widgetSelect = widget?.querySelector('.oke-sortSelect-sort, #oke-sortSelect--reviews');
    if (!controls || !widgetSortWrap || !widgetSelect) return;

    controls.innerHTML = '';
    const clone = widgetSortWrap.cloneNode(true);
    stripIds(clone);

    const cloneSelect = clone.querySelector('.oke-sortSelect-sort, #oke-sortSelect--reviews');
    if (cloneSelect) {
      setSelectOrderBy(cloneSelect, widgetSelect.value);
      const activeSelect = hijackSortSelect(section, cloneSelect, (replacement) => {
        const orderBy = normalizeOrderBy(replacement.value);
        if (orderBy === normalizeOrderBy(section.dataset.okendoCurrentSort)) return;
        setSelectOrderBy(widgetSelect, orderBy);
        sortReviews(section, orderBy, 'modal');
      });
      setupMobileSortPicker(section, activeSelect, (replacement) => {
        const orderBy = normalizeOrderBy(replacement.value);
        if (orderBy === normalizeOrderBy(section.dataset.okendoCurrentSort)) return;
        setSelectOrderBy(widgetSelect, orderBy);
        sortReviews(section, orderBy, 'modal');
      });
    }

    controls.appendChild(clone);
  };

  const copyReviewsToModal = (section) => {
    const widget = getWidget(section);
    const modal = getModal(section);
    const sourceList = widget?.querySelector('.oke-w-reviews-list');
    const targetList = modal?.querySelector('.okendo-reviews-modal__list');
    if (!sourceList || !targetList) return;

    targetList.innerHTML = '';
    sourceList.querySelectorAll('.oke-w-reviews-list-item').forEach((item) => {
      targetList.appendChild(item.cloneNode(true));
    });

    const footer = widget.querySelector('.oke-w-reviews-footer');
    const showMoreHref = footer?.querySelector('.oke-showMore a, .oke-showMore-button')?.getAttribute('href');
    const nextUrl = parseNextUrl(showMoreHref);
    updateModalShowMore(section, nextUrl, 2);
  };

  const lockBodyScroll = () => {
    document.body.style.overflow = 'hidden';
  };

  const unlockBodyScroll = () => {
    document.body.style.overflow = '';
  };

  const openReviewsModal = (section) => {
    const widget = getWidget(section);
    const modal = getModal(section);
    if (!widget || !modal) return;

    resolveReviewsApiBase(section, widget);

    copyReviewsToModal(section);
    setupModalSort(section, widget, modal);

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    section.dataset.okendoModalOpen = 'true';
    lockBodyScroll();

    const panel = modal.querySelector('.okendo-reviews-modal__panel');
    const closeBtn = modal.querySelector('.okendo-reviews-modal__close');
    if (panel) panel.focus();
    else if (closeBtn) closeBtn.focus();
  };

  const closeReviewsModal = (section) => {
    const modal = getModal(section);
    if (!modal || section.dataset.okendoModalOpen !== 'true') return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-open');
    delete section.dataset.okendoModalOpen;
    unlockBodyScroll();
  };

  const setupModal = (section) => {
    if (section.dataset.okendoModalInit === 'true') return;
    section.dataset.okendoModalInit = 'true';

    const modal = getModal(section);
    if (!modal) return;

    modal.querySelectorAll('[data-okendo-modal-close]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        closeReviewsModal(section);
      });
    });
  };

  const setupLoadMore = (section) => {
    if (section.dataset.okendoLoadMoreInit === 'true') return;
    section.dataset.okendoLoadMoreInit = 'true';

    section.addEventListener(
      'click',
      (event) => {
        const modalTrigger = event.target.closest('.okendo-reviews-modal__load-more');
        if (modalTrigger && section.contains(modalTrigger)) {
          event.preventDefault();
          event.stopPropagation();
          loadMoreReviews(section, modalTrigger, 'modal');
          return;
        }

        const trigger = event.target.closest(
          '.oke-showMore a, a.oke-showMore-button, .oke-showMore-button'
        );
        if (!trigger || !section.contains(trigger)) return;

        const widget = getWidget(section);
        if (!widget?.contains(trigger)) return;

        event.preventDefault();
        event.stopPropagation();
        openReviewsModal(section);
      },
      true
    );
  };

  const hijackSortSelect = (section, select, onChange) => {
    if (!select || select.dataset.okendoSortHijacked === 'true') return select;

    const replacement = select.cloneNode(true);
    replacement.dataset.okendoSortHijacked = 'true';
    select.replaceWith(replacement);

    replacement.addEventListener(
      'change',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        onChange(replacement, event);
      },
      true
    );

    return replacement;
  };

  const setupSort = (section) => {
    const widget = getWidget(section);
    let select = widget?.querySelector('.oke-sortSelect-sort, #oke-sortSelect--reviews');
    if (!select || section.dataset.okendoSortInit === 'true') return;

    section.dataset.okendoSortInit = 'true';

    resolveReviewsApiBase(section, widget);

    const defaultSort = normalizeOrderBy(
      getMetafieldData(widget)?.sort?.defaultSort ||
        select.querySelector('option[selected]')?.value ||
        select.value ||
        'date desc'
    );
    section.dataset.okendoCurrentSort = defaultSort;
    setSelectOrderBy(select, defaultSort);

    select = hijackSortSelect(section, select, (activeSelect) => {
      const orderBy = normalizeOrderBy(activeSelect.value);
      if (orderBy === normalizeOrderBy(section.dataset.okendoCurrentSort)) return;
      sortReviews(section, orderBy, 'inline');
    });

    setupMobileSortPicker(section, select, (activeSelect) => {
      const orderBy = normalizeOrderBy(activeSelect.value);
      if (orderBy === normalizeOrderBy(section.dataset.okendoCurrentSort)) return;
      sortReviews(section, orderBy, 'inline');
    });
  };

  const setupEscapeClose = () => {
    if (window.Eurus.okendoReviewsEscapeInit) return;
    window.Eurus.okendoReviewsEscapeInit = true;

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('.okendo-sort-picker.is-open').forEach(closeMobileSortPicker);
      document.querySelectorAll('.okendo-reviews[data-okendo-modal-open="true"]').forEach((section) => {
        closeReviewsModal(section);
      });
    });
  };

  const watchSection = (section) => {
    if (section.dataset.okendoWatchInit === 'true') return;
    section.dataset.okendoWatchInit = 'true';

    dismissLoadingAlerts(section);
    setupModal(section);
    setupLoadMore(section);
    setupSort(section);
    watchReviewsList(section);
    setupEscapeClose();

    setTimeout(() => dismissLoadingAlerts(section), 1500);
    setTimeout(() => dismissLoadingAlerts(section), 4000);
  };

  const boot = () => {
    document.querySelectorAll('.okendo-reviews').forEach(watchSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section =
      event.target.querySelector?.('.okendo-reviews') || event.target.closest?.('.okendo-reviews');
    if (section) watchSection(section);
  });
}
